import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { GET_ALL_DEALERS } from '../schemas/DealerLocationSchema';
import { AudiDealershipLocation } from '../types/dealershipLocations';
import useLocationLocalStorage from './useLocationLocalStorage';
import {
  setUserAndRadiusLocation,
  selectDealersList,
  setDealers,
  setPartner,
} from '../reducers/dealersListReducer';
import { MARKET } from '../constants/market';
import { selectDealerContext } from '../reducers/dealerContextReducer';
import { GEO_ZIP, PARTNER_ID } from '../constants/locationConstants';
import { useGetLocationByZip } from './useGetLocationByZip';
import { getAvailableDealers } from '../utils/getAvailableDealers';

export interface GeoJSONProperties {
  id: string;
  name: string;
  phone: string;
  phoneURL: string;
  email: string;
  kvpsSyncId: string;
  address: {
    city: string;
    latitude: number;
    longitude: number;
    region: string;
    street: string;
    zipCode: string;
  };
  kvpsid: string;
}

type UseDealershipList = () => {
  geoJson: GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>;
  loading: boolean;
};

interface QueryResult {
  dealersByMarket: {
    dealers: AudiDealershipLocation[];
  };
}

const useDealershipList: UseDealershipList = () => {
  const dispatch = useDispatch();
  const { initialData, saveDealersToLS } = useLocationLocalStorage();
  const { allDealersList } = useSelector(selectDealersList);
  const { dealerContextId } = useSelector(selectDealerContext);

  const { zipCoordinates } = useGetLocationByZip(GEO_ZIP);

  console.log('ZIP COORDINATES HERE ------>>>>', zipCoordinates);

  const [getDealers, { data: dealers, loading, called }] = useLazyQuery<QueryResult>(
    GET_ALL_DEALERS,
    {
      errorPolicy: 'all',
      context: {
        headers: { clientid: 'wv2ybzheub' },
      },
    },
  );

  useEffect(() => {
    getDealers({
      variables: {
        // A- is for "Audi"
        market: `A-${MARKET}`,
      },
    });
  }, [getDealers]);

  const geoJson: GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties> = {
    type: 'FeatureCollection',
    features:
      dealers?.dealersByMarket.dealers.map((dealer) => {
        const { latitude, longitude, street, houseNumber, city, zipCode, region } = dealer;
        const address = {
          latitude,
          longitude,
          street: `${street}${houseNumber ? ` ${houseNumber}` : ''}`,
          city,
          zipCode,
          region,
        };

        return {
          type: 'Feature',
          properties: {
            id: dealer.kvpsId,
            name: dealer.name,
            address,
            kvpsSyncId: dealer.kvpsId,
            kvpsid: dealer.kvpsId,
            phone: dealer.phone,
            phoneURL: dealer.phoneURL,
            email: dealer.email,
          },
          geometry: {
            type: 'Point',
            coordinates: [longitude, latitude],
          },
        };
      }) || [],
  };

  // ASK
  if (geoJson.features.length && Object.keys(allDealersList).length === 0) {
    const allDealers: GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties> | any = geoJson;
    dispatch(setDealers(allDealers));
    if (
      (PARTNER_ID || dealerContextId) &&
      (allDealers as GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>).features?.length
    ) {
      const selectedPartner = (
        allDealers as GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>
      ).features.find(
        (dealer) =>
          dealer.properties.kvpsSyncId === PARTNER_ID ||
          dealer.properties.kvpsSyncId === dealerContextId,
      );
      if (selectedPartner) {
        // Save selected dealer to LS
        saveDealersToLS({
          isPreSetPartner: false,
          selectedDealershipList: [selectedPartner.properties],
          isPartnerHeader: true,
          selectedRadius: MARKET === 'CAN' ? 150 : 50,
          selectedSearchMode: 'BY_DEALERS',
          selectedUserLocation: {
            feature: {
              geometry: {
                location: {
                  lat: selectedPartner.properties.address.latitude,
                  lng: selectedPartner.properties.address.longitude,
                },
              },
              name: selectedPartner.properties.name,
              formattedAddress: selectedPartner?.properties.name,
              region: selectedPartner?.properties.address.region,
              postalCode: selectedPartner?.properties.address.zipCode,
              city: selectedPartner?.properties.address.city,
            },
            userCoordinates: {
              latitude: selectedPartner.properties.address.latitude,
              longitude: selectedPartner.properties.address.longitude,
            },
          },
        });
        dispatch(setPartner(selectedPartner));
      }
    }

    if (
      initialData.invalidDataFromLs &&
      GEO_ZIP &&
      zipCoordinates &&
      (allDealers as GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>).features?.length
    ) {
      console.log(' ------>>>> TRIGGER TO SET COORDINATES BY ZIP <<<<------ ');
      const latitude = zipCoordinates.geometry.location.lat;
      const longitude = zipCoordinates.geometry.location.lng;
      const centerLocation = {
        geometry: {
          location: {
            lat: latitude,
            lng: longitude,
          },
        },
      };
      const dealersWithinRadius = getAvailableDealers({
        allDealersList: allDealers,
        centerLocation,
        radius: 50,
      }).map((dealer) => dealer.properties);

      const locationToSet = {
        isPreSetPartner: false,
        isPartnerHeader: false,
        selectedRadius: 50,
        selectedDealershipList: dealersWithinRadius,
        selectedSearchMode: 'BY_RADIUS',
        selectedUserLocation: {
          feature: zipCoordinates,
          userCoordinates: {
            latitude,
            longitude,
          },
        },
      };

      dispatch(setUserAndRadiusLocation(locationToSet));
      saveDealersToLS(locationToSet);
    }

    if (!initialData.invalidDataFromLs && (!PARTNER_ID || !dealerContextId)) {
      console.log(' ------>>>> TRIGGER TO SET COORDINATES BY SELECTOR <<<<------ ');
      dispatch(setUserAndRadiusLocation(initialData));
    }
  }

  return { geoJson, loading: !called || loading };
};

export default useDealershipList;
